import { connect } from 'react-redux';
import { FormApi } from 'final-form';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useFormState } from 'react-final-form';
import { isValid, max, min, startOfDay } from 'date-fns';

import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { createKnmEventInspectorSelectOption } from 'CorePackage/CatalogsModule/common/createKnmEventInspectorSelectOption';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { ErknmEventInspectors } from 'KnmErknmPackage/ErknmEventsModule/components/ErknmEventInspectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmDataErknmApproveAppeal,
  getKnmDataErknmApproveApproved,
  getKnmDataVersion,
  getKnmIsErknmStatusTypeWaitingCarryOut,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmEventSubmittedDocumentsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmEventSubmittedDocumentsForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEventProtocol } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEventProtocol';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ErknmEventUav } from 'KnmErknmPackage/ErknmEventsModule/components/ErknmEventUav';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';

import styles from './KnmActEventItemBody.module.scss';
import { KnmActEventItemBodyProtocol } from './KnmActEventItemBodyProtocols/KnmActEventItemBodyProtocol';
import { useDebouncedValue } from '../../../hooks/useDebouncedMemo';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { applyActEventCopyNumbersToForm, getActEventTypeDisplayLabel } from './getActEventTypeDisplayLabel';
import { IKnmErknmFormExpert } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { ErknmActItemEventExperts } from '../../../KnmErknmPackage/ErknmActsModule/components/ErknmActItemEventExperts/ErknmActItemEventExperts';

interface IErknmActEventItemBodyProps {
  eventId: string;
  eventFieldName: string;
  eventFieldValue: IKnmErknmFormEvent;
  expertsErknm?: IKnmErknmFormExpert[];
  isWatchOnly: boolean;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
  stopDate: string | undefined;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  isDecision?: boolean;
  isPm?: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  actIndex: number | undefined;
  eventIndex: number;
  value: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
  actLevelName?: string;
}

interface IErknmActEventItemBodyStoreProps {
  isKnmStatusTypeWaitingCarryOut: boolean;
  knmDataApproveAppeal: boolean | null | undefined;
  knmDataApproved: ApprovedTypesEnum | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  isErp: boolean;
}

const ErknmActEventItemBodyWithoutStore = ({
  eventId,
  eventFieldName,
  eventFieldValue,
  expertsErknm,
  isWatchOnly,
  knmEventTypesSelectOptions,
  setUniqKnmEventTypesSelectOptions,
  stopDate,
  uniqKnmEventTypesSelectOptions,
  isKnmStatusTypeWaitingCarryOut,
  knmDataApproveAppeal,
  knmDataApproved,
  knmPlanStatus,
  formRef,
  isDecision,
  isPm,
  actIndex,
  eventIndex,
  value,
  actLevelName,
  isErp,
}: IErknmActEventItemBodyProps & IErknmActEventItemBodyStoreProps) => {
  const [inspectorOptions, setInspectorOptions] = useState<ISelectOption[]>([]);
  const [isWithoutActions, setWithoutAction] = useState<boolean | undefined>(
    eventFieldValue.type ? eventFieldValue.type.label === 'Осуществляется без действия' : undefined,
  );
  const remove = formRef?.current.mutators.remove;
  const isRequestForDocuments =
    !!eventFieldValue.type && eventFieldValue.type.label.indexOf('Истребование документов') > -1;

  const eventsFieldName = useMemo(() => {
    if (isPm) {
      return 'eventsPm';
    }

    if (isDecision) {
      return 'events';
    }

    return 'eventsErknm';
  }, [isPm, isDecision]);

  const { values } = useFormState({
    subscription: { values: true },
  });

  const isDP = useMemo(() => values?.kindKnm?.code === 'DP', [values?.kindKnm]);
  // Есть ли хотя бы одно действие, которое будет заполнено
  const isFillAction = useMemo(() => {
    return value.events?.findIndex(
      e => !e.actionNotPerformed && !!e.type?.label.length && e.type?.label !== 'Осуществляется без действия',
    );
  }, value?.events);

  const {
    input: { value: isUavUsed },
  } = useField<boolean | undefined>(`${eventFieldName}.isUavUsed`, { subscription: { value: true } });

  // @ts-ignore
  const isUnableToRefuse: boolean | undefined = formRef?.current.getFieldState('isUnableToRefuse')?.value;

  const isInspection = eventFieldValue.type ? eventFieldValue.type.label === 'Осмотр' : undefined;
  const isSampling = eventFieldValue.type ? eventFieldValue.type.label === 'Отбор проб (образцов)' : undefined;

  const isShowErknmUav =
    (values?.kindKnm?.code === KnmKindCodesEnum.IV ||
      values?.kindKnm?.code === KnmKindCodesEnum.RO ||
      values?.kindKnm?.code === KnmKindCodesEnum.VP) &&
    (isInspection || isSampling) &&
    !isPm &&
    !isDecision &&
    !isErp;

  const isShowPmUav = isPm && isUnableToRefuse && (isInspection || isSampling) && !isErp;

  // Ожидаем изменения в перечне действий
  const eventsErknmDebounced = useDebouncedValue<IKnmErknmFormEvent[]>(values?.[eventsFieldName] || [], 1000);

  useEffect(() => {
    setWithoutAction(eventFieldValue.type ? eventFieldValue.type.label === 'Осуществляется без действия' : undefined);
  }, [eventFieldValue.type]);

  const isShowVideoLinkFields = useMemo(() => {
    if (eventFieldValue.type?.label) {
      const knmEvent = eventsErknmDebounced?.find(i => i.type?.title === eventFieldValue.type?.label);

      return knmEvent?.interactionCategory && knmEvent.interactionCategory.id !== 3;
    }
  }, [eventFieldValue.type?.label, eventsErknmDebounced]);

  const isActiveTypeAndDates =
    isKnmStatusTypeWaitingCarryOut &&
    knmDataApproved === ApprovedTypesEnum.APPROVED &&
    !!knmDataApproveAppeal &&
    knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED;

  const isShowEventExperts =
    eventFieldValue?.type &&
    (eventFieldValue.type.label === 'Отбор проб (образцов)' ||
      eventFieldValue.type.label === 'Экспертиза' ||
      eventFieldValue.type.label === 'Испытание');

  const requestForDocumentsEvents = useMemo(
    () => eventsErknmDebounced?.filter(i => i.type?.title && i.type?.title.indexOf('Истребование документов') > -1),
    [JSON.stringify(eventsErknmDebounced)],
  );

  const allSubmittedDocuments = useMemo(() => {
    const submittedDocumentsResult: IKnmErknmEventSubmittedDocumentsForm[] = [];

    requestForDocumentsEvents?.forEach(i => {
      i.submittedDocuments?.forEach(doc => {
        submittedDocumentsResult.push(doc);
      });
    });

    return submittedDocumentsResult;
  }, [JSON.stringify(eventsErknmDebounced)]);

  const currentEventSubmittedDocuments = useMemo(
    () =>
      eventFieldValue.type
        ? requestForDocumentsEvents?.find(
            e => e.type?.value?.dictVersionId === eventFieldValue?.type?.value?.dictVersionId,
          )?.submittedDocuments
        : undefined,
    [requestForDocumentsEvents, eventFieldValue.type],
  );

  useEffect(() => {
    let inspectors: IKnmErknmFormInspector[] | undefined;

    if (isDecision) {
      inspectors = formRef?.current.getState().values.inspectors as IKnmErknmFormInspector[];
    } else {
      inspectors = (formRef?.current.getState().values as IKnmForm).inspectorsErknm;
    }

    inspectors?.forEach(x => {
      if (x.fullName && x.guid && !inspectorOptions?.map(i => i.value).includes(x.fullName)) {
        const option = createKnmEventInspectorSelectOption(x.fullName, x.guid);

        inspectorOptions.push(option);
        setInspectorOptions(inspectorOptions);
      }
    });
  }, [formRef]);

  const handleSetEventType = (
    val: IDictionaryEventTypeSelectOption | undefined,
    clearByActionNotPerformed?: boolean,
  ) => {
    // Из-за несвоевременной очистки формы при change, она ругается.
    // Поэтому списки experts и inspectors очищаем через remove
    const expertsFieldName = `${eventFieldName}.experts`;
    const expertsValue = (formRef?.current as FormApi<IKnmForm>).getFieldState(expertsFieldName as keyof IKnmErknmForm)
      ?.value;

    if (Array.isArray(expertsValue) && remove) {
      for (let index = expertsValue.length - 1; index >= 0; index -= 1) {
        remove(expertsFieldName, index);
      }
    }

    const inspectorsFieldName = `${eventFieldName}.inspectors`;
    const inspectorsValue = (formRef?.current as FormApi<IKnmForm>).getFieldState(
      inspectorsFieldName as keyof IKnmErknmForm,
    )?.value;

    if (Array.isArray(inspectorsValue) && remove) {
      for (let index = inspectorsValue.length - 1; index >= 0; index -= 1) {
        remove(inspectorsFieldName, index);
      }
    }

    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.interactionCategory` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.startDate` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.stopDate` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.submittedDocuments` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.linkVks` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.linkVideo` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.actionDescription` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.actionDescriptionReactQuillEditor` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.isUavUsed` as keyof IKnmErknmForm,
    );
    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.uavTechnologies` as keyof IKnmErknmForm,
    );

    const protocol = {
      file: undefined,
      appendicesToTheProtocolOfAction: [],
      studiedDocuments:
        val?.label && val?.label.indexOf('Истребование документов') > -1 ? currentEventSubmittedDocuments : undefined,
    };

    (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
      `${eventFieldName}.protocol` as keyof IKnmErknmForm,
      // @ts-ignore
      val ? protocol : undefined,
    );

    if (!clearByActionNotPerformed) {
      (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
        `${eventFieldName}.actionNotPerformed` as keyof IKnmErknmForm,
      );
    }

    if (actLevelName) {
      const actEventsFieldName = `${actLevelName}.events`;
      const events: IKnmErknmFormEvent[] | undefined = (formRef as IKnmFormRef)?.current.getFieldState(
        actEventsFieldName as keyof IKnmForm,
      )?.value as IKnmErknmFormEvent[] | undefined;

      if (events) {
        applyActEventCopyNumbersToForm((formRef as IKnmFormRef)?.current, actEventsFieldName, events);
      }
    }

    setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
  };

  const formatActEventTypeOptionLabel = useCallback(
    (option: IDictionaryEventTypeSelectOption, { context }: { context: 'menu' | 'value' }) => {
      if (context === 'menu') {
        return option.label;
      }

      return getActEventTypeDisplayLabel(option.label, eventFieldValue.eventCopyNumber);
    },
    [eventFieldValue.eventCopyNumber],
  );

  const updateProtocol = useCallback(
    (data?: IKnmErknmFormEventProtocol) => {
      const studiedDocuments = isRequestForDocuments ? currentEventSubmittedDocuments : undefined;

      const protocol = data
        ? {
            ...data,
            studiedDocuments,
          }
        : {
            file: undefined,
            appendicesToTheProtocolOfAction: [],
            studiedDocuments,
          };

      (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
        `${eventFieldName}.protocol` as keyof IKnmErknmForm,
        protocol as IKnmErknmFormEventProtocol,
      );
    },
    [isRequestForDocuments, currentEventSubmittedDocuments],
  );

  const isCanNotBeDeletedAction = useMemo(() => {
    return (
      !!allSubmittedDocuments?.find(document => ['SD-1', 'SD-3'].includes(document?.status?.code || '')) &&
      isRequestForDocuments
    );
  }, [allSubmittedDocuments, isRequestForDocuments]);

  // Инициализируем предоставляемые документы в действие "Истребование документов"
  useEffect(() => {
    if (isRequestForDocuments && allSubmittedDocuments?.length) {
      if (!eventFieldValue?.protocol) {
        handleSetEventType(eventFieldValue.type, true);
      } else {
        updateProtocol(eventFieldValue.protocol);
      }

      // Если хоть одно действие представлено, то "Действие не проводилось" должно быть отключено
      if (isCanNotBeDeletedAction && !!eventFieldValue.actionNotPerformed) {
        (formRef?.current as FormApi<IKnmForm, Partial<IKnmForm>>).change(
          `${eventFieldName}.actionNotPerformed` as keyof IKnmErknmForm,
          false,
        );
      }
    }
  }, [isRequestForDocuments, allSubmittedDocuments]);

  const basisDocumentDateTime = isDecision ? values?.basisDocumentDateTime : undefined;
  const actHoldingDateTime = isDecision ? values?.organization?.act?.holdingDateTime : value.holdingDateTime;
  const mainEvents = values?.[eventsFieldName];
  const mainStartDateValue = values?.startDate;
  const mainStopDateValue = values?.stopDate;

  const { minStartDate, maxStartDate, minStopDate, maxStopDate } = useMemo(() => {
    const knmMainEvents = mainEvents as IKnmErknmFormEvent[] | undefined;
    const mainStartDateString = mainStartDateValue;
    const mainStopDateString = mainStopDateValue;

    const knmMainEvent = knmMainEvents?.find(
      (event: IKnmErknmFormEvent) => event.type?.title === eventFieldValue.type?.label,
    );

    const mainStartDate = parseDate({
      date: mainStartDateString || '',
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    const mainStopDate = parseDate({
      date: mainStopDateString || '',
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    const minDateByMainEvent = parseDate({
      date: knmMainEvent?.startDate || '',
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    const maxDateByMainEvent = parseDate({
      date: knmMainEvent?.stopDate || '',
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    const minDateByHoldingDateTime = parseDate({
      date: actHoldingDateTime || '',
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });

    const maxStopDateResult = min([maxDateByMainEvent, mainStopDate].filter((date): date is Date => isValid(date)));
    const maxStartDateResult = max(
      [maxStopDateResult, minDateByHoldingDateTime].filter((date): date is Date => isValid(date)),
    );

    const minDateStart = max([mainStartDate, minDateByMainEvent].filter((date): date is Date => isValid(date)));
    const defaultMinDate = isValid(minDateStart) ? minDateStart : undefined;
    const basisDocumentDate = parseDate({
      date: basisDocumentDateTime || '',
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });

    const decisionMinDates = [defaultMinDate, minDateByHoldingDateTime, basisDocumentDate]
      .filter((date): date is Date => date !== undefined && isValid(date))
      .map(date => startOfDay(date));

    const decisionMinDate = decisionMinDates.length ? max(decisionMinDates) : undefined;
    const baseDate = isDecision && isValid(decisionMinDate) ? decisionMinDate : defaultMinDate;
    const eventStartDateString = eventFieldValue.startDate;

    let baseStopDate = baseDate;

    if (eventStartDateString) {
      const parseEventStartDateString = parseDate({
        date: eventStartDateString?.toString(),
        parseFormat: DATE_FORMAT_LONG_FNS,
      });

      baseStopDate = max([baseDate, parseEventStartDateString].filter((date): date is Date => isValid(date)));
    }

    const validMaxStartDate = isValid(maxStartDateResult) ? maxStartDateResult : undefined;
    const validMaxStopDate = isValid(maxStopDateResult) ? maxStopDateResult : undefined;

    return {
      minStartDate: baseDate,
      maxStartDate: isDecision ? undefined : validMaxStartDate,
      minStopDate: baseStopDate,
      maxStopDate: isDecision ? undefined : validMaxStopDate,
    };
  }, [
    mainEvents,
    mainStartDateValue,
    mainStopDateValue,
    eventFieldValue.type?.label,
    eventFieldValue.startDate,
    actHoldingDateTime,
    basisDocumentDateTime,
    isDecision,
  ]);

  const handleActionNotPerformedChange = (v: ChangeEvent<HTMLInputElement>) => {
    if (v.target.checked) {
      handleSetEventType(undefined, true);
    } else {
      updateProtocol();
    }
  };

  const onChangeIsUavUsed = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      // @ts-ignore
      formRef?.current.change(`${eventFieldName}.uavTechnologies`);
    }
  };

  return (
    <>
      <Row>
        <FieldBlock id={`${eventId}.type`}>
          <SelectInputField
            name={`${eventFieldName}.type`}
            placeholder={getTitleFieldWithRedStar({
              label: 'Выберите действие',
              field: `${eventFieldName}.type`,
              isPm,
              isDecision,
              isKnm: !isPm && !isDecision,
            })}
            options={uniqKnmEventTypesSelectOptions}
            formatOptionLabel={formatActEventTypeOptionLabel}
            disabled={
              (isWatchOnly && !isActiveTypeAndDates) ||
              isCanNotBeDeletedAction ||
              eventFieldValue.autoInnerEventForAddingFlag
            }
            size="large"
            onChange={e => handleSetEventType(e)}
            warning={
              // Если не выбран тип "Без действия" в документарной проверке, то показываем предупреждение (при условии, что еще не выбрано ни одно действие к заполнению)
              !(isDP && isWithoutActions) && isFillAction
                ? 'Фиксация нарушений возможна в случае проведения действий (кроме документарной проверки, где возможен выбор "без действия")'
                : undefined
            }
          />
        </FieldBlock>
      </Row>
      {eventFieldValue.type?.label && !(isDP && isWithoutActions) && (
        <div className={styles.CheckboxField}>
          <CheckboxField
            id={`${eventFieldName}.actionNotPerformed`}
            name={`${eventFieldName}.actionNotPerformed`}
            onChange={handleActionNotPerformedChange}
            disabled={
              (isWatchOnly && !isActiveTypeAndDates) ||
              isCanNotBeDeletedAction ||
              eventFieldValue.autoInnerEventForAddingFlag
            }
            theme="system"
            type="checkbox"
            label={'Действие не проводилось'}
            color="black"
          />
        </div>
      )}
      {isDP && isWithoutActions && (
        <Row>
          <FieldBlock
            id={`${eventId}.actionDescription`}
            label={''}
            color="black"
            placementLabel="left"
            labelSize="col-3"
            className={styles.CustomMarginBottom}
          >
            <ReactQuillEditorField
              id={`${eventFieldName}.actionDescriptionReactQuillEditor`}
              name={`${eventFieldName}.actionDescriptionReactQuillEditor`}
              placeholder="Введите сведения"
              disabled={isWatchOnly && !isActiveTypeAndDates}
              handleCustomChange={(e: string) => {
                // @ts-ignore
                formRef?.current?.change(`${eventFieldName}.actionDescription` as string, e);
              }}
              className={styles.TextField}
              warning={'Заполните сведения, которые изучались в ходе мероприятия'}
            />
          </FieldBlock>
        </Row>
      )}
      {!eventFieldValue.actionNotPerformed && eventFieldValue.type?.label && (
        <>
          {isWithoutActions === false && (
            <Row>
              <FieldBlock id={`${eventId}.startDate`} size="half" margin="right">
                <DatePickerField
                  name={`${eventFieldName}.startDate`}
                  placeholder={getTitleFieldWithRedStar({
                    label: 'Дата начала',
                    field: `${eventFieldName}.startDate`,
                    isPm,
                    isDecision,
                    isKnm: !isPm && !isDecision,
                  })}
                  disabled={isWatchOnly && !isActiveTypeAndDates}
                  minDate={minStartDate}
                  maxDate={maxStartDate}
                />
              </FieldBlock>
              <FieldBlock id={`${eventId}.stopDate`} size="half">
                <DatePickerField
                  name={`${eventFieldName}.stopDate`}
                  key={stopDate}
                  placeholder={getTitleFieldWithRedStar({
                    label: 'Дата окончания',
                    field: `${eventFieldName}.stopDate`,
                    isPm,
                    isDecision,
                    isKnm: !isPm && !isDecision,
                  })}
                  disabled={isWatchOnly && !isActiveTypeAndDates}
                  minDate={minStopDate}
                  maxDate={maxStopDate}
                />
              </FieldBlock>
            </Row>
          )}
          {isShowVideoLinkFields && (
            <FieldBlock
              id={`${eventId}.linkVks`}
              label={getTitleFieldWithRedStar({
                label: 'Ссылка на ВКС',
                field: `${eventFieldName}.linkVks`,
                isPm,
                isDecision,
                isKnm: !isPm && !isDecision,
              })}
              color="black"
              placementLabel="left"
              labelSize="col-3"
              margin="bottom"
            >
              <TextInputField
                id={`${eventFieldName}.linkVks`}
                name={`${eventFieldName}.linkVks`}
                placeholder="Введите ссылку"
                disabled={isWatchOnly && !isActiveTypeAndDates}
                className={styles.TextField}
              />
            </FieldBlock>
          )}
          <FieldBlock
            id={`${eventId}.linkVideo`}
            label={'Ссылка на видеозапись'}
            color="black"
            placementLabel="left"
            labelSize="col-3"
            className={styles.CustomMarginBottom}
          >
            <TextInputField
              id={`${eventFieldName}.linkVideo`}
              name={`${eventFieldName}.linkVideo`}
              placeholder="Введите ссылку"
              disabled={isWatchOnly && !isActiveTypeAndDates}
              className={styles.TextField}
            />
          </FieldBlock>
          <ErknmEventInspectors
            isPm={isPm}
            isDecision={isDecision}
            name={`${eventFieldName}.inspectors`}
            eventId={eventId}
            watchOnly={isWatchOnly}
            value={value}
            actLevelName={actLevelName}
          />
          {expertsErknm && isShowEventExperts && (
            <ErknmActItemEventExperts
              actEventId={eventId}
              expertsErknm={expertsErknm}
              formRef={formRef}
              involvedExperts={eventFieldValue?.experts}
              isWatchOnly={isWatchOnly}
              name={`${eventFieldName}.experts`}
            />
          )}
        </>
      )}
      {eventFieldValue.type?.label && !eventFieldValue.actionNotPerformed && eventFieldValue.protocol && (
        <KnmActEventItemBodyProtocol
          eventId={eventId}
          name={`${eventFieldName}.protocol`}
          watchOnly={isWatchOnly}
          isRequestForDocuments={isRequestForDocuments && !eventFieldValue.actionNotPerformed}
          actIndex={actIndex}
          eventIndex={eventIndex}
          formRef={formRef}
          isPm={isPm}
          isDecision={isDecision}
          attachmentsNumber={eventFieldValue.protocol?.file?.attachments?.length || 0}
          eventFieldValueWithoutAction={eventFieldValue.type?.label === 'Осуществляется без действия'}
          protocolValue={eventFieldValue.protocol}
        />
      )}
      {(isShowErknmUav || isShowPmUav) && (
        <Row>
          <FieldBlock>
            <CheckboxField
              className={styles.FilterFormGroupCheckbox}
              id={`${eventId}.isUavUsed`}
              name={`${eventFieldName}.isUavUsed`}
              label="Проводились с использованием беспилотных технологий"
              theme="system"
              type="checkbox"
              size="medium"
              onChangeCallback={onChangeIsUavUsed}
              disabled={isWatchOnly && !isActiveTypeAndDates}
            />
          </FieldBlock>
          {isUavUsed && (
            <ErknmEventUav
              id={`${eventId}.uavTechnologies`}
              name={`${eventFieldName}.uavTechnologies`}
              watchOnly={isWatchOnly}
              isDecision={isDecision}
              isPm={isPm}
            />
          )}
        </Row>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  knmDataApproveAppeal: getKnmDataErknmApproveAppeal(state),
  knmDataApproved: getKnmDataErknmApproveApproved(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  isErp: getKnmDataVersion(state) === KnmVersionsEnum.ERP,
});

export const KnmActEventItemBody = connect(mapStateToProps)(ErknmActEventItemBodyWithoutStore);
